// The cross-vocabulary partition: WHICH stall kind classifies each hold reason.
// Split from `./hold.ts`, which is at the 800-line ratchet, and because this is
// the DECISION between two vocabularies, not part of what a hold IS — the same
// shape `hold-machine.ts` took for its own reader's classification (#6231).
//
// THE TWO VOCABULARIES. `TaskHoldReason` (`./hold`) is what a dispatcher pass
// STAMPED on `tasks.hold_reason`; `TaskStallKind` (`./stall`) is what the
// queue-stall report DERIVES at read time. Neither file can state the mapping:
// `hold.ts` imports only zod, `stall.ts` never imports `./hold`, and the
// classifier that resolves one kind per ROW needs the decision as SQL. For the
// reasons the classifier covers, two readers already imply the mapping:
//
//   - the SQL CASE in `stallPredicateSql`
//     (`packages/db/src/queries/tasks/stalls.ts`), whose arms read every
//     classified hold reason off the stamp and carry all six folds below —
//     `key_reserved` into the `KeySaturated` arm, `provider_capacity` into
//     `PoolCooling`, `review_awaiting_model` into `CredentialCooling`,
//     `provider_key_refused` and `no_screen_key` into `NoUsableKeys`, and
//     `no_capable_box` into `NoServiceCapacity` as its stamp-only disjunct —
//     while the derived arms recompute the same facts the same-named kinds
//     carry. A row parked on any fold answers its kind on the report; and
//   - the precedence intersection
//     `apps/worker/src/dispatch/assigner-empty-claim-writers.test.ts:125-128`
//     builds — `TASK_STALL_PRECEDENCE` filtered to the members `TaskHoldReason`
//     also holds — which spans the shared names and never the folds.
//
// This table states that mapping ONCE, total and decided: the two readers above
// both carry it — every fold is read off the stamp by its arm, the same-named
// members by theirs — and for the five folds and the two exemptions this table
// was the first statement of the decision, which the classifier's arms and the
// guard test now implement and pin. It is also where the NEXT decision gets
// written, so a new hold reason arrives with its classification rather than
// implying one.
//
// THE CONTRACT. `TASK_HOLD_STALL` is total over `TaskHoldReason` — a new hold
// reason does not compile until someone decides which stall kind classifies it,
// or writes the `why` that justifies `kind: null`. THREE decision classes, the
// sets pinned by `hold-stall.test.ts`:
//
//   - IDENTITY (19): the same-named member of both enums — the classifier's arm
//     for the kind IS the hold's fact, whether read off the stamp or re-derived
//     from the same stored rows the writer read.
//   - FOLD (6): a hold reason classified under a DIFFERENTLY-named kind — the
//     `key_reserved`-into-`key_saturated` precedent (#6711): the stored VALUE
//     stays distinct and every reader but the report still sees it; the report
//     names the shared kind instead. All six are live in `stallPredicateSql`:
//     `key_reserved` in the `KeySaturated` arm, `provider_capacity` in the
//     `PoolCooling` arm, `review_awaiting_model` in the `CredentialCooling` arm,
//     `provider_key_refused` and `no_screen_key` in the `NoUsableKeys` arm, and
//     `no_capable_box` as the `NoServiceCapacity` arm's stamp-only disjunct. The
//     entry's `why` states the story, so the fold is a citation, never a silent rename.
//   - EXEMPTION (2, `kind: null`): an EXPLICIT, justified exemption — the `why`
//     carries the ruling that the wait pages nobody who can act. Never a gap:
//     the `Record` type refuses a missing member and the guard refuses a null
//     without its justification.
//
// THE DERIVED-ONLY kinds — `dependency_failed`, `blocked_on_answer`,
// `dependency_pending`, `dependency_unreleasable`, `no_box_on_account` — map from
// NO hold reason: they derive from rows that are not a hold stamp (the DAG, an
// open blocker, the account's runner rows), which is the whole reason the second
// vocabulary exists beside the column (hold.ts, the NULL-column paragraph). The
// test pins that set too, so a kind can neither go unmapped-and-undecided nor
// quietly become a hold classification without a decision written here.

import { TaskHoldReason } from './hold';
import { TaskStallKind } from './stall';

/**
 * One hold reason's classification: the stall kind that names its wall on the
 * derived report, or `null` for a justified exemption — with the `why` either
 * way, so the decision travels with the table.
 */
export type TaskHoldStall = {
  /**
   * The stall kind that classifies this hold reason. `null` is an EXPLICIT
   * justified exemption — the `why` quotes the ruling — never an unclassified
   * member: the `Record` over the enum refuses the hole, and
   * `hold-stall.test.ts` refuses an exemption that does not state its case.
   */
  readonly kind: TaskStallKind | null;
  /** Why this classification: the shared fact, the fold's story, or the ruling. */
  readonly why: string;
};

/**
 * Every hold reason, classified. See the module header for the contract and the
 * three decision classes; `hold-stall.test.ts` pins each set.
 */
export const TASK_HOLD_STALL: Readonly<Record<TaskHoldReason, TaskHoldStall>> = {
  // ─── IDENTITY: the same-named member of both enums ──────────────────────────
  [TaskHoldReason.NoUsableKeys]: {
    kind: TaskStallKind.NoUsableKeys,
    why: 'the same fact by the same name: the account has a key pool and zero usable keys. Read off the stamp (the first stamp-read arm in `stallPredicateSql`) because the pool is dispatcher state no stored row re-derives.',
  },
  [TaskHoldReason.AreaBusy]: {
    kind: TaskStallKind.AreaBusy,
    why: 'the same #513 same-area gate: the writer and the derived arm both build on `areaFreeSql`, so a row the claim withholds as busy is exactly the row the report names busy.',
  },
  [TaskHoldReason.NoConverseCapacity]: {
    kind: TaskStallKind.NoConverseCapacity,
    why: 'the same interactive-capacity gate, derived from `noConverseCapacitySql` over the origin and the account runner rows the writer read — so a pending row no pass reached is still explained.',
  },
  [TaskHoldReason.NoServiceCapacity]: {
    kind: TaskStallKind.NoServiceCapacity,
    why: 'the same #2919 services gate, derived from `noServiceCapacitySql` — built from the same `repoServicesRequired` fragment `claimNextReady` narrows with, so the column and the report cannot disagree.',
  },
  [TaskHoldReason.RepoNotOnboarded]: {
    kind: TaskStallKind.RepoNotOnboarded,
    why: 'the same onboarding gate, derived from `repoNotOnboardedSql` — the same fragment `claimNextReady` narrows with and `markRepoNotOnboardedHolds` stamps from.',
  },
  [TaskHoldReason.PlanPaused]: {
    kind: TaskStallKind.PlanPaused,
    why: 'the same epic pause, derived from `planPausedSql()` — the fragment `claimNextReady` narrows with and `markPlanPausedHolds` stamps from (#6748).',
  },
  [TaskHoldReason.MachinePinned]: {
    kind: TaskStallKind.MachinePinned,
    why: 'the same machine pin, derived from the box-independent `taskPinnedSql()` — the fragment `markMachinePinnedHolds` stamps from; the claim enforces its per-box `<> runner` half inline (#6748).',
  },
  [TaskHoldReason.UpstreamGroupUnmerged]: {
    kind: TaskStallKind.UpstreamGroupUnmerged,
    why: 'the same cross-group release rule, derived from `upstreamGroupUnmergedSql`; it tops `TASK_STALL_PRECEDENCE` so the stamped column and the report agree on rows both gates hold.',
  },
  [TaskHoldReason.UpstreamLegUndeployed]: {
    kind: TaskStallKind.UpstreamLegUndeployed,
    why: 'the same deploy gate of a `deployed` edge, derived from `upstreamLegUndeployedSql` (one arm of `upstreamEdgeUnreleasedSql`, the claim conjunct); directly below `upstream_group_unmerged` in `TASK_STALL_PRECEDENCE`, so a row both arms hold reads the unmerged cause on both sides.',
  },
  [TaskHoldReason.SeatCapped]: {
    kind: TaskStallKind.SeatCapped,
    why: 'the same #3630 cap, read off the stamp (the second stamp-read arm): which slots are running is the assigner concurrency gate over the live claim set, which no stored row re-derives.',
  },
  [TaskHoldReason.PoolCooling]: {
    kind: TaskStallKind.PoolCooling,
    why: 'the same upstream pool wall, read off the stamp (the third stamp-read arm): the cooldown is a Dragonfly record with a TTL, so a derived predicate would have to invent a row.',
  },
  [TaskHoldReason.BoxFaultExcluded]: {
    kind: TaskStallKind.BoxFaultExcluded,
    why: 'the same three box-fault conditions (the baseline exclusion, the decode hold #6237, the memory cap #7081), derived from the row fields their writers wrote — the report answers for a pending row no empty-claim pass reached.',
  },
  [TaskHoldReason.ModeDisabled]: {
    kind: TaskStallKind.ModeDisabled,
    why: 'the same agent-mode gate, derived from `modeDisabledSql()` — the same repo > org > default order the claim resolves, pinned to the origin-to-mode resolve by test.',
  },
  [TaskHoldReason.NoRoleMachine]: {
    kind: TaskStallKind.NoRoleMachine,
    why: 'the same machine-role gate, derived from `noRoleMachineSql()` — the fragment `markNoRoleMachineHolds` stamps from; the claim enforces its box-local half, `machineRoleOffSql`.',
  },
  [TaskHoldReason.CredentialCooling]: {
    kind: TaskStallKind.CredentialCooling,
    why: 'the same resolver verdict, read off the stamp and its live window: the claim skips exactly the rows the report names, and a lapsed window is a row the next claim re-decides.',
  },
  [TaskHoldReason.RepoEnvFault]: {
    kind: TaskStallKind.RepoEnvFault,
    why: 'the same repo-env outage, read off the stamp and its live window (#4914): stamp + live window IS the gate, so a predicate over the fault row instead would call the released probe held.',
  },
  [TaskHoldReason.ResumeBackoff]: {
    kind: TaskStallKind.ResumeBackoff,
    why: 'the same parked ladder with saved work, read off the stamp and its live window; `parkExhaustedTask` is the only writer of both.',
  },
  [TaskHoldReason.KeySaturated]: {
    kind: TaskStallKind.KeySaturated,
    why: 'the same full-key wall, read off the stamp and its live window: the verdict needs the task in hand and each serving key in-flight units, claim-time state no stored row re-derives.',
  },
  [TaskHoldReason.SelectionWaiting]: {
    kind: TaskStallKind.SelectionWaiting,
    why: 'the same selection wait, read off the stamp and its live window: the lane waits on its own Agents-page key while other keys may be idle — never an account outage.',
  },
  [TaskHoldReason.PrQuietWindow]: {
    kind: TaskStallKind.PrQuietWindow,
    why: 'the same human-push quiet window, read off the stamp and its live window; the box `human_pushing` report is the only writer of both.',
  },
  [TaskHoldReason.EnvFaultExhausted]: {
    kind: TaskStallKind.EnvFaultExhausted,
    why: 'the same spent ladder held with its blocker, read off the stamp and its live window; `holdExhaustedTask` is the only writer of both.',
  },
  // ─── FOLD: classified under a differently-named kind; the value stays distinct ──
  [TaskHoldReason.KeyReserved]: {
    kind: TaskStallKind.KeySaturated,
    why: 'the documented fold (#6711): a free slot held for higher-priority work reads, to the report, as waiting for a key slot — one kind, told apart on the row own `hold_reason`, not by a second stall kind. The stored value stays distinct; the fold is read-time only, the precedent every other fold here cites.',
  },
  [TaskHoldReason.ReviewAwaitingModel]: {
    kind: TaskStallKind.CredentialCooling,
    why: 'the review-scoped key-wall-with-window (plan 101 K3/K4): parked until `hold_until` equals the earliest refused key reset, woken by a key turning `valid` — the same story `credential_cooling` tells, for the credential-reading lanes with no box-env floor. The stored value stays distinct; the fold is decided here and carried by the `CredentialCooling` arm of `stallPredicateSql`, live window included — the report names such a row instead of answering it `queued`.',
  },
  [TaskHoldReason.ProviderKeyRefused]: {
    kind: TaskStallKind.NoUsableKeys,
    why: 'a quota-spent or dead key IS a key out of the pool (plan 101 L3). The NOT-NoUsableKeys ruling in the hold docblock governs the WRITER mechanics — `tasks_hold_until_ck` admits no window beside `no_usable_keys`, so a windowed re-pend could not stamp that value — not the story. The stored value stays distinct exactly as `key_reserved` does; the fold is decided here and carried by the `NoUsableKeys` arm of `stallPredicateSql`, live-windowed — a lapsed `hold_until` is a row the next claim re-decides.',
  },
  [TaskHoldReason.ProviderCapacity]: {
    kind: TaskStallKind.PoolCooling,
    why: 'the same capacity refusal with a stated reset, one layer down (the #6501 hot-loop fix): the provider or pool refused THIS run for capacity and dated the wall — the per-task windowed arm of the pool wall `pool_cooling` names account-wide. The key is fine, which is why no key change releases it; the wall is still the pool recovery window. The stored value stays distinct.',
  },
  [TaskHoldReason.NoScreenKey]: {
    kind: TaskStallKind.NoUsableKeys,
    why: 'the zero-usable-key fact, screen-scoped (plan 101 09-10): every non-donated key is out of the pool, so the screen has nothing to run on — the same every-key-out-of-the-pool story `no_usable_keys` states, woken by the same `releaseHoldsOnKeyValid`. The stored value stays distinct.',
  },
  [TaskHoldReason.NoCapableBox]: {
    kind: TaskStallKind.NoServiceCapacity,
    why: 'a required capability token no online box advertises is the services gate superset: `noServiceCapacitySql` already fires exactly on the `service:*` subset of the same question, so the kind remedy carries the general action — bring online a box advertising the missing token, or drop the requirement — and its `.maintainer.yml`-specific clauses scope to that subset. The stored value stays distinct.',
  },
  // ─── EXEMPTION: kind null, justified — pages nobody who can act ─────────────
  [TaskHoldReason.CiPending]: {
    kind: null,
    why: 'EXPLICIT EXEMPTION, by the ruling in the hold own docblock (#6718): the wait is self-clearing and bounded by the CI ceiling, owned by CI, the sweep and the clock — a stall report naming it would page nobody who can act.',
  },
  [TaskHoldReason.ErrorIssuePending]: {
    kind: null,
    why: 'EXPLICIT EXEMPTION, the same bounded, self-clearing platform-actor wait the `ci_pending` ruling exempts: windowed by the mint own `ERROR_ISSUE_HOLD_MS` (`./error-issue-hold`), retracted by `linkErrorIssueTasks` on a filing or on a cap, refusal or fault, and a lapsed window lets the task dispatch unlinked, with no sweep. It pages nobody who can act, so a stall kind would be a page with no reader.',
  },
};
